/**
 * /quiz-buzzer — clock sync between the host and each phone.
 *
 * The question the whole tool answers is «who pressed first», and the network
 * lies about it: a phone on a slow Wi-Fi corner delivers its press 150 ms
 * after a phone next to the router, so arrival order punishes the slower
 * network, not the slower hand. Every press is therefore stamped ON THE PHONE
 * (the input event's own timestamp) and the host translates that stamp into
 * its own clock.
 *
 * Translation needs the offset between the two monotonic clocks. The host asks
 * NTP-style: it sends its time h0, the phone answers with its time p, the
 * answer lands at h1. The phone's clock read p somewhere between h0 and h1, so
 *
 *     offset = phone − host  ∈  [p − h1, p − h0]
 *
 * holds for EVERY exchange, whatever the delays and however asymmetric they
 * are, as long as delays are not negative. Instead of trusting one sample (the
 * classic «take the minimum round trip») we intersect the intervals of all
 * recent samples: the result is at least as tight as the best single sample,
 * usually tighter, and it comes with an honest error bound — half its width.
 *
 * Two real-world effects are handled explicitly:
 *   · drift — two quartz clocks walk apart by tens of ppm, so the offset is not
 *     a constant but a line: offset(t) = a + r·t. Every exchange bounds that
 *     line at one moment, and the set of lines consistent with ALL recent
 *     exchanges is a convex polygon in (a, r) — we keep it exactly (clipping a
 *     box |r| ≤ 500 ppm by two half-planes per exchange) and read the offset
 *     range at any moment off its vertices. A constant rate inside the box can
 *     never be excluded, so the truth stays inside the bound; and because the
 *     rate is learned instead of assumed, an old exchange keeps helping instead
 *     of being widened into uselessness;
 *   · jumps — a phone that reloads the page (new time origin) or whose clock
 *     stalls in sleep produces exchanges that no line fits. An empty polygon is
 *     proof of that, and the older exchanges are dropped.
 *
 * What cannot be known from round trips alone is the ASYMMETRY of a path that
 * is always asymmetric (uplink always slower than downlink): the midpoint of
 * the interval then sits off by half the difference. That residue is exactly
 * what `err` reports, and the page shows it instead of pretending to be exact.
 *
 * Pure: no DOM, no timers. Tested by tests/js/quiz-buzzer-clock.test.mjs with
 * simulated asymmetric, jittery and drifting links.
 */

export const DRIFT = 5e-4;      // 500 ppm: the widest rate the polygon allows (quartz ±50, NTP slew ±500)
export const RES_MS = 1;        // timer coarsening allowance on each side

/* Clip a convex polygon [[a, r], ...] by the half-plane  s·(a + r·x) ≥ s·b
   (s = +1 keeps a + r·x ≥ b, s = −1 keeps a + r·x ≤ b). Sutherland–Hodgman. */
function clip(poly, x, b, s) {
    const out = [];
    const n = poly.length;
    if (!n) return out;
    const f = (v) => s * (v[0] + v[1] * x - b);
    for (let i = 0; i < n; i++) {
        const P = poly[i], Q = poly[(i + 1) % n];
        const fp = f(P), fq = f(Q);
        if (fp >= 0) out.push(P);
        if ((fp >= 0) !== (fq >= 0)) {
            const k = fp / (fp - fq);
            out.push([P[0] + (Q[0] - P[0]) * k, P[1] + (Q[1] - P[1]) * k]);
        }
    }
    return out;
}

export function createClockSync(opts) {
    opts = opts || {};
    const R = opts.drift != null ? opts.drift : DRIFT;
    const res = opts.resMs != null ? opts.resMs : RES_MS;
    const maxN = opts.window || 32;
    const maxAge = opts.maxAgeMs || 180000;
    let samples = [];           // oldest first: {h0, h1, tm, lo, hi, rtt}
    let poly = null;            // feasible (a, r) around the reference below
    let ref = null;             // {t, c}: offsets are a + r·(t − ref.t) + ref.c
    let est = null;             // {offset, err, n, at}
    let jumps = 0;

    function range(t) {
        if (!poly || !poly.length) return null;
        let lo = Infinity, hi = -Infinity;
        const x = t - ref.t;
        for (const v of poly) { const y = v[0] + v[1] * x; if (y < lo) lo = y; if (y > hi) hi = y; }
        return { lo: lo + ref.c, hi: hi + ref.c };
    }

    function solve(now) {
        const last = samples[samples.length - 1];
        ref = { t: last.tm, c: (last.lo + last.hi) / 2 };
        const W = 1e7;          // a is unbounded a priori; the first exchange pins it
        let P = [[-W, -R], [W, -R], [W, R], [-W, R]];
        let used = 0;
        for (let i = samples.length - 1; i >= 0; i--) {
            const s = samples[i], x = s.tm - ref.t;
            const Q = clip(clip(P, x, s.lo - ref.c, 1), x, s.hi - ref.c, -1);
            if (Q.length < 3 && used > 0) {
                // No drift line fits this exchange together with the newer ones:
                // the clock jumped between them. Newer wins; older goes.
                samples = samples.slice(i + 1);
                jumps++;
                break;
            }
            P = Q; used++;
        }
        poly = P;
        const r = range(now);
        est = r ? { offset: (r.lo + r.hi) / 2, err: Math.max(0, (r.hi - r.lo) / 2), n: used, at: now } : null;
    }

    function add(h0, p, h1) {
        if (!(Number.isFinite(h0) && Number.isFinite(p) && Number.isFinite(h1)) || h1 < h0) return false;
        samples.push({ h0: h0, h1: h1, tm: (h0 + h1) / 2, lo: p - h1 - res, hi: p - h0 + res, rtt: h1 - h0 });
        while (samples.length > maxN || (samples.length > 1 && h1 - samples[0].h1 > maxAge)) samples.shift();
        solve(h1);
        return true;
    }

    function recent(k) { return samples.slice(-k); }
    function offsetAt(t) { const r = range(t); return r ? (r.lo + r.hi) / 2 : NaN; }

    return {
        add: add,
        ready: function () { return !!est; },
        /** Exchanges that agree with each other (the current estimate rests on them). */
        count: function () { return est ? est.n : 0; },
        jumps: function () { return jumps; },
        offset: function (t) { return est ? (t == null ? est.offset : offsetAt(t)) : 0; },
        /** Error bound at host time `now` — read off the polygon, so it grows by the unknown part of the drift. */
        errAt: function (now) {
            if (!est) return Infinity;
            const r = range(now == null ? est.at : now);
            return r ? Math.max(0, (r.hi - r.lo) / 2) : Infinity;
        },
        estimate: function () { return est ? Object.assign({}, est) : null; },
        rttMin: function () {
            if (!samples.length) return NaN;
            let m = Infinity; for (const s of samples) if (s.rtt < m) m = s.rtt; return m;
        },
        /** High-water round trip of the last few exchanges — what a press may plausibly need to arrive. */
        rttHigh: function (k) {
            const r = recent(k || 5);
            if (!r.length) return NaN;
            let m = 0; for (const s of r) if (s.rtt > m) m = s.rtt; return m;
        },
        rttLast: function () { return samples.length ? samples[samples.length - 1].rtt : NaN; },
        /** Round trips of the exchanges the estimate rests on (oldest first) — for diagnostics. */
        rtts: function () { return samples.map(function (s) { return s.rtt; }); },
        /** Phone time → host time (the offset read at host time `at`, default: the phone time itself). */
        toHost: function (p, at) { if (!est) return NaN; const o = offsetAt(at != null ? at : p - est.offset); return p - o; },
        /** Host time → phone time. */
        toLocal: function (h) { return est ? h + offsetAt(h) : NaN; },
        reset: function () { samples = []; poly = null; est = null; }
    };
}

/**
 * Translate a phone's press stamp into host time and decide how far to trust it.
 *
 *   p       — the press as stamped on the phone (its performance.now scale)
 *   recvAt  — host time the press arrived
 *
 * A stamp is only as good as the clock estimate behind it, and a phone could
 * also simply lie (edited script). The physical bound is the uplink: the press
 * cannot have reached us before it was made, and it cannot have spent longer on
 * the way than this link has been seen to take. A stamp outside that bound is
 * clamped to the bound and flagged, so a stale estimate or a forged stamp can
 * gain at most one round trip plus `slackMs` — never the whole grace window.
 *
 * Returns { t, err, how, claimed } — `how` is 'stamp' | 'future' | 'slow' | 'unsynced'.
 */
export function correctPress(sync, p, recvAt, o) {
    o = o || {};
    const slack = o.slackMs != null ? o.slackMs : 300;
    const minUp = o.minMaxUpMs != null ? o.minMaxUpMs : 400;
    if (!sync || !sync.ready()) {
        // No clock yet (the press came before the first round trip finished):
        // arrival minus a typical one-way trip is the best we have, and we say so.
        const up = o.fallbackUpMs != null ? o.fallbackUpMs : 40;
        return { t: recvAt - up, err: up, how: 'unsynced', claimed: NaN };
    }
    const claimed = sync.toHost(p, recvAt);
    const err = sync.errAt(recvAt);
    const rttMin = sync.rttMin(), rttHigh = sync.rttHigh();
    const maxUp = Math.max(minUp, rttHigh + slack);
    const up = recvAt - claimed;
    if (up < -err - 2) {
        // Stamped after it arrived: impossible for a real press.
        return { t: recvAt - rttMin / 2, err: rttMin / 2, how: 'future', claimed: claimed };
    }
    if (up > maxUp) {
        // Claims a longer trip than this link ever takes: counted by arrival.
        return { t: recvAt - maxUp, err: err, how: 'slow', claimed: claimed };
    }
    return { t: Math.min(claimed, recvAt), err: err, how: 'stamp', claimed: claimed };
}

/**
 * How long the host waits after the earliest press before naming the winner.
 * A press made earlier by a slower phone must still be on its way; it arrives
 * within that phone's uplink, which is bounded by its round trip. We wait for
 * the slowest connected phone's recent high round trip plus slack — clamped so
 * a single terrible link cannot make every question feel laggy.
 */
export function graceFor(syncs, o) {
    o = o || {};
    const lo = o.minMs != null ? o.minMs : 150, hi = o.maxMs != null ? o.maxMs : 600;
    const slack = o.slackMs != null ? o.slackMs : 150;
    let worst = 0, any = false;
    for (const s of syncs) {
        if (!s) continue;
        any = true;
        const r = s.ready() ? s.rttHigh() : NaN;
        worst = Math.max(worst, Number.isFinite(r) ? r + slack : hi);
    }
    if (!any) return 0;          // nobody on a network: every stamp is local and exact
    return Math.round(Math.min(hi, Math.max(lo, worst)));
}

/**
 * Lead before a scheduled opening, so every screen can light at the same host
 * instant: the opening must reach the slowest phone before it happens.
 */
export function leadFor(syncs, o) {
    o = o || {};
    const lo = o.minMs != null ? o.minMs : 120, hi = o.maxMs != null ? o.maxMs : 700;
    let worst = 0, any = false;
    for (const s of syncs) {
        if (!s) continue;
        any = true;
        const r = s.ready() ? s.rttHigh() : NaN;
        worst = Math.max(worst, Number.isFinite(r) ? r + 60 : hi);
    }
    if (!any) return 0;
    return Math.round(Math.min(hi, Math.max(lo, worst)));
}
